import { Component } from '@acies/core';
import { type DrawingSide, fitViewBox, SHOW_INSIDE_CLASS } from '../../../domain/drawings';
import { ConfiguratorModel } from '../configurator.model';

/**
 * Shows the drawing the Configurator is working on, in a shadow root of its own.
 *
 * The only component here with a shadow root, and it has one for what the drawings carry: 93 `id` attributes apiece, referred to by `href="#MeasureArrow"` and `filter="url(#blurfilter)"`, with names as generic as `0`, `1` and `field-number-background`. Two drawings in one document would collide on every one of them,
 * and a reference in the second would resolve to the first. A shadow tree scopes both the ids and the references, so nothing has to be rewritten.
 *
 * The second thing it buys is the stylesheets. A drawing arrives with no styling of its own — its appearance is carried by class names like `frame`, `sash` and `measurement` — so what styles it is a stylesheet fetched beside it. Adopted into the shadow root, that sheet reaches the drawing and nothing else on the page.
 *
 * Follows `ConfiguratorModel` itself — the drawing, the side it is turned to and the sheets that style it — rather than being handed them by whatever it sits in. It is shown in more than one place, and each place would otherwise have to wire up the same three subscriptions. Deciding which drawing belongs in the model,
 * and fetching it, stays `ConfiguratorMachine`'s: an answer that arrives late must be dropped rather than shown, and only something that knows what the user has selected since can tell.
 *
 * The getters are there to read what it follows, and nothing sets them from outside.
 */
export default class DrawingViewer extends Component {
	/**
	 * The drawing the model holds, which the viewer shows a copy of, or null when there is none.
	 *
	 * The model's own element and not the copy, which only the shadow root holds — see #commitDrawing for why it is a copy.
	 */
	public get drawing(): SVGSVGElement | null {
		return this.#drawing;
	}

	/**
	 * Which side of the window the drawing shows.
	 *
	 * Applied as one class on the drawing itself rather than as a `display` on each of its four groups — see {@link SHOW_INSIDE_CLASS} — so a drawing with more groups than we expect turns around just the same.
	 */
	public get side(): DrawingSide {
		return this.#side;
	}

	/**
	 * The stylesheets the drawing is styled by, in the order they are adopted.
	 *
	 * Taken as a list rather than as named parts, so the viewer never has to know what each one is for: which sheet may override which is decided where they are read, by `getDrawingStyles`.
	 */
	public get styles(): ReadonlyArray<CSSStyleSheet> {
		return this.#styles;
	}

	#drawing: SVGSVGElement | null;
	#drawingChanged: boolean;
	/** The copy in the shadow root, which is what is turned around and measured. */
	#shown: SVGSVGElement | null;
	#resizeObserver: ResizeObserver;
	#shadow: ShadowRoot;
	#side: DrawingSide;
	#sideChanged: boolean;
	#styles: ReadonlyArray<CSSStyleSheet>;
	#stylesChanged: boolean;

	public constructor() {
		super();
		this.#drawing = null;
		this.#drawingChanged = false;
		this.#shown = null;
		// In the constructor, unlike every other DOM call here, and deliberately: attaching a
		// shadow root is one of the few things a custom element constructor is allowed to do, and
		// doing it here means the root exists before anything can be committed into it. Appending
		// to it is still left to a commit.
		this.#shadow = this.attachShadow({ mode: 'open' });
		this.#side = 'outside';
		this.#sideChanged = false;
		this.#styles = [];
		this.#stylesChanged = false;
		this.height = 'fill';
		this.width = 'fill';
		// the listeners are registered on the model and on the observer, so they would otherwise run
		// in their scope rather than in this one
		this.drawingChanged = this.drawingChanged.bind(this);
		this.sideChanged = this.sideChanged.bind(this);
		this.stylesChanged = this.stylesChanged.bind(this);
		this.resized = this.resized.bind(this);
		this.#resizeObserver = new ResizeObserver(this.resized);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds. What it already holds is taken straight
		// after, so a viewer built after a drawing had landed shows it all the same.
		ConfiguratorModel.currentDrawing.subscribe(this.drawingChanged);
		ConfiguratorModel.currentDrawingSide.subscribe(this.sideChanged);
		ConfiguratorModel.currentDrawingStyles.subscribe(this.stylesChanged);
		this.stylesChanged(ConfiguratorModel.currentDrawingStyles.value);
		this.sideChanged(ConfiguratorModel.currentDrawingSide.value);
		this.drawingChanged(ConfiguratorModel.currentDrawing.value);
	}

	/**
	 * Clips whatever the drawing paints outside the viewer, and starts watching its own size.
	 *
	 * What `Container.clipContent` does for a container of components, except that this one always clips, so there is nothing to make a property of. `hidden` and not the `auto` that `clipContent` sets: the drawing is positioned to fill the viewer, so anything outside is outside in every direction at once, and half of
	 * it is above and left of the origin where no scrollbar can reach it. Offering to scroll to it would be offering nothing.
	 *
	 * The drawing itself keeps `overflow: visible` — the two rules are a pair. Its measurement arrows are drawn outside the viewBox on purpose, so the drawing must be allowed to paint past its own viewport; what it must not do is paint past the section it sits in. So the viewBox does not clip and the viewer does.
	 *
	 * Set here rather than in the stylesheet the viewer adopts, because a viewer that was handed no stylesheet would otherwise paint over the rest of the app. Clipping is what this element is, not something it is styled with.
	 *
	 * The size is watched for the one moment a drawing cannot be fitted on its own: see {@link DrawingViewer.resized}.
	 */
	protected override initialize(): void {
		super.initialize();
		this.style.overflow = 'hidden';
		this.#resizeObserver.observe(this);
	}

	/**
	 * Unsubscribes and stops watching, because the model outlives the viewer.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a viewer that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentDrawing.unsubscribe(this.drawingChanged);
		ConfiguratorModel.currentDrawingSide.unsubscribe(this.sideChanged);
		ConfiguratorModel.currentDrawingStyles.unsubscribe(this.stylesChanged);
		this.#resizeObserver.disconnect();
	}

	/**
	 * Styles first, then the drawing.
	 *
	 * Both in one pass, so the order within it is the whole guarantee: a drawing is never in the document for a frame before the sheets that style it. Set the other way round, a new drawing would show unstyled until the next commit — which for one that arrives with its styles already adopted would be no commit at all.
	 */
	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#stylesChanged) {
			this.#commitStyles();
		}
		if (this.#sideChanged) {
			this.#commitSide();
		}
		if (this.#drawingChanged) {
			this.#commitDrawing();
		}
	}

	/** Takes whatever drawing the model now holds, which is nothing when it holds nothing. */
	private drawingChanged(drawing: SVGSVGElement | null): void {
		this.#drawing = drawing;
		this.#drawingChanged = true;
		this.invalidateProperties();
	}

	/** Turns the drawing to whichever side the model now says. */
	private sideChanged(side: DrawingSide): void {
		this.#side = side;
		this.#sideChanged = true;
		this.invalidateProperties();
	}

	/** Takes whatever sheets the drawing is to be styled by now. */
	private stylesChanged(styles: ReadonlyArray<CSSStyleSheet>): void {
		this.#styles = styles;
		this.#stylesChanged = true;
		this.invalidateProperties();
	}

	/**
	 * Puts a copy of the drawing in the shadow root, in place of whatever was there.
	 *
	 * A copy, because the same drawing is shown in more than one place: inserting an element moves it, so the model's own element in two viewers would end up in whichever committed last and leave the other empty. Each viewer copying for itself means none of them can take the drawing from another, and the model's
	 * element is never put in a document at all — which also keeps the side and the viewBox each viewer sets on its copy out of what the others show.
	 *
	 * `replaceChildren` for both halves of that at once: the previous drawing goes, and inserting the copy adopts it out of the document `parseDrawing` left it in. Called with nothing when there is no drawing, which empties the root — a viewer showing no drawing rather than one still showing the last.
	 *
	 * The side is applied and the viewBox fitted after the insertion, not before: a drawing can only be measured once it is in a rendered document. See `fitViewBox`, which is where the reason the engine's own viewBox will not do is written down.
	 */
	#commitDrawing(): void {
		this.#drawingChanged = false;
		if (this.#drawing === null) {
			this.#shown = null;
			this.#shadow.replaceChildren();
			return;
		}
		this.#shown = this.#drawing.cloneNode(true) as SVGSVGElement;
		this.#shadow.replaceChildren(this.#shown);
		this.#showSide();
	}

	/** Turns whatever is on screen around, which is nothing when there is no drawing. */
	#commitSide(): void {
		this.#sideChanged = false;
		this.#showSide();
	}

	/**
	 * Shows the chosen side of the drawing, and measures it again.
	 *
	 * Measured again because the two sides do not draw the same thing: each has its own dimension marks, and what is hidden is left out of the box `fitViewBox` reads. Turning the drawing around therefore changes how much room it needs.
	 */
	#showSide(): void {
		if (this.#shown === null) {
			return;
		}
		this.#shown.classList.toggle(SHOW_INSIDE_CLASS, this.#side === 'inside');
		fitViewBox(this.#shown);
	}

	/**
	 * Fits the drawing again when the viewer's size changes.
	 *
	 * For the viewer that was out of the layout when its drawing arrived — the one in a dialog nobody has opened yet. A drawing in a hidden viewer measures nothing, so `fitViewBox` leaves the engine's own viewBox alone, and nothing else would ask it again once the viewer is shown. Being shown is a change of size, from
	 * none to some, so this is where it is fitted after all.
	 */
	private resized(): void {
		if (this.#shown !== null) {
			fitViewBox(this.#shown);
		}
	}

	/**
	 * Adopts the sheets, replacing the ones adopted before.
	 *
	 * Copied into an array of its own, because `adoptedStyleSheets` is a live list the browser reads from: handing it the model's array would let a later change to that array reach in here. The sheets themselves are shared on purpose — one `CSSStyleSheet` styles every viewer on the page.
	 */
	#commitStyles(): void {
		this.#stylesChanged = false;
		this.#shadow.adoptedStyleSheets = [...this.#styles];
	}
}
customElements.define('ac-drawing-viewer', DrawingViewer);
